import React, { useState, useEffect } from "react";
import {
  Typography,
  Card,
  Table,
  Space,
  Button,
  Tag,
  notification,
  Popconfirm,
} from "antd";
import {
  FolderViewOutlined,
  MedicineBoxOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import { casesService } from "../services/casesService";
import { CasesDTO } from "../models/Cases";
import styles from "./Dashboard.module.css";
import { useAuth } from "../contexts/authContext";
import { SortOrder } from "antd/es/table/interface";
import { routes } from "../routes";
import { isAslDoctor, isSpecialist } from "../config/utils";
import { useLoaderProvider } from "../contexts/LoaderContext";

const { Title } = Typography;

const Dashboard: React.FC = () => {
  const { showLoader, hideLoader } = useLoaderProvider();
  const [cases, setCases] = useState<CasesDTO[]>([]);
  const [loading, setLoading] = useState(false);
  // const [refreshCases, setRefreshCases] = useState<boolean>(false);
  const { loggedDoctor, loggedUser } = useAuth();
  const isAsl = isAslDoctor(loggedUser!);
  const isSpecialistDoctor = isSpecialist(loggedUser!);
  const navigate = useNavigate();

  const takingCase = async (caseId: number) => {
    try {
      showLoader("Presa in carico del caso...");
      await casesService.changeStatus(caseId, "IN_PROGRESS");
      navigate(`${routes.CASE_DETAILS.replace(":id", caseId.toString())}`);
      //setRefreshCases(!refreshCases);
    } catch (error) {
      console.error("Errore durante la conferma del caso:", error);
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    const fetchCases = async () => {
      setLoading(true);
      try {
        const paramsAsl = {
          page: 0,
          size: 5,
          sort: ["createdAt,desc"],
          "doctorsId.equals": loggedDoctor!.id,
        };
        const paramsSpecialist = {
          page: 0,
          size: 5,
          sort: ["createdAt,desc"],
          "caseStatus.equals": "OPEN",
        };

        const response = await casesService.getAllCases(
          isAsl ? paramsAsl : (paramsSpecialist as any)
        );
        setCases(response);
      } catch (error) {
        notification.error({ message: "Errore nel caricamento dei casi" });
        console.error("Errore nel caricamento dei casi:", error);
      } finally {
        setLoading(false);
      }
    };

    if (loggedDoctor) fetchCases();
  }, [isAsl, loggedDoctor /*, refreshCases*/]);

  const columns = [
    {
      title: "ID Caso",
      dataIndex: "id",
      key: "id",
      onCell: () => ({ "data-label": "ID Caso" } as any),
    },
    {
      title: "Nome Paziente",
      key: "patientName",
      onCell: () => ({ "data-label": "Nome Paziente" } as any),
      render: (text: any, record: CasesDTO) =>
        `${record.patient?.firstName} ${record.patient?.lastName}`,
    },
    {
      title: "Stato",
      dataIndex: "caseStatus",
      key: "caseStatus",
      onCell: () => ({ "data-label": "Stato" } as any),
      render: (caseStatus: "DRAFT" | "OPEN" | "IN_PROGRESS" | "CLOSED") => {
        switch (caseStatus) {
          case "DRAFT":
            return <Tag color={"blue"}>BOZZA</Tag>;
          case "OPEN":
            return <Tag color={"green"}>APERTO</Tag>;
          case "IN_PROGRESS":
            return <Tag color={"orange"}>PRESO IN CARICO</Tag>;
          case "CLOSED":
            return <Tag color={"red"}>CHIUSO</Tag>;
          default:
            return "";
        }
      },
    },
    {
      title: "Data Creazione",
      dataIndex: "createdAt",
      defaultSortOrder: "descend" as SortOrder,
      sorter: (a: CasesDTO, b: CasesDTO) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(), // Ordinamento corretto
      key: "createdAt",
      onCell: () => ({ "data-label": "Data Creazione" } as any),
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Azioni",
      key: "actions",
      onCell: () => ({ "data-label": "Azioni" } as any),
      render: (_: any, record: CasesDTO) => (
        <>
          <Button
            type="default"
            icon={<FolderViewOutlined />}
            onClick={() => handleViewCase(record.id!)}
          >
            Visualizza
          </Button>
          {isSpecialistDoctor && (
            <Popconfirm
              title="Prendi in carico"
              description="Sei sicuro di voler prendere in carico il caso?"
              onConfirm={() => takingCase(record.id!)}
              okText="Si"
              cancelText="No"
            >
              <Button
                style={{ marginLeft: "10px" }}
                type="primary"
                icon={<MedicineBoxOutlined />}
              >
                Prendi in carico
              </Button>
            </Popconfirm>
          )}
        </>
      ),
    },
  ];

  const handleViewCase = (caseId: number) => {
    navigate(routes.CASE_DETAILS.replace(":id", caseId.toString()));
  };

  return (
    <div className={styles.dashboardContainer}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div className={styles.headerContainer}>
          <Title level={2}>Dashboard</Title>
          {isAsl && (
            <Link to={`${routes.CASE_MANAGEMENT}`}>
              <Card className={styles.createCaseCard} hoverable>
                <PlusOutlined /> CREA CASO
              </Card>
            </Link>
          )}
        </div>

        <div className={styles.dashboardImageparentContainer}>
          <div className={styles.dashboardImageContainer}></div>
        </div>

        <Table
          className={styles.responsiveTable}
          columns={columns}
          dataSource={cases}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Space>
    </div>
  );
};

export default Dashboard;
